/**
 * Speculative prerendering of monolith (Rails) destinations for Arc schools.
 *
 * Leaving the SPA for a Rails page always costs a full document load. The
 * Speculation Rules API lets the browser prerender those destinations when the
 * user hovers a link (`eagerness: 'moderate'`), so the SPA→monolith transition
 * feels near-instant on supporting browsers (Chromium 121+).
 *
 * Risks / limits, by design:
 * - Prerendering consumes extra bandwidth, CPU and memory on the user's
 *   device. `moderate` eagerness bounds the cost: the browser only prerenders
 *   on hover/pointerdown (and caps concurrent prerenders) instead of
 *   prefetching every link on the page.
 * - Rules use an explicit allowlist of the monolith paths linked from Arc
 *   screens (see the navigation audit); routes with session side effects
 *   (logout, profile switch, account flows) are additionally excluded so a
 *   speculative load can never mutate the user's session.
 * - Silent no-op on browsers without Speculation Rules support.
 */

/**
 * Monolith destinations linked from Arc screens (URLPattern syntax; `*` is a
 * greedy wildcard). SPA routes must never appear here — they navigate through
 * react-router and a prerender would be wasted.
 */
export const MONOLITH_PRERENDER_PATTERNS = [
  '/students/*',
  '/group_school_terms/*',
  '/session_meetings/*',
  '/end_of_term_reports/*',
  '/manually_register/*',
  '/projects_library',
  '/projects_library/*',
  '/projects/*/mural_items',
  '/projects/*/mural_items/*',
  '/projects/*/final_report',
  '/projects/*/final_assessments',
  '/curriculums',
  '/schools',
  '/transfer_students',
  '/manager/dashboard',
]

/**
 * Never prerender these: navigations with session side effects
 * (signin/signout/signup flows, profile switching, Devise endpoints).
 */
export const PRERENDER_EXCLUDED_PATTERNS = [
  '/account/*',
  '/profiles/*',
  '/users/*',
  '/login',
]

/** Pure builder for the `speculationrules` JSON payload. */
export const buildSpeculationRules = () => ({
  prerender: [
    {
      where: {
        and: [
          { or: MONOLITH_PRERENDER_PATTERNS.map(pattern => ({ href_matches: pattern })) },
          { not: { or: PRERENDER_EXCLUDED_PATTERNS.map(pattern => ({ href_matches: pattern })) } },
        ],
      },
      eagerness: 'moderate',
    },
  ],
})

const SCRIPT_ID = 'arc-monolith-speculation-rules'

type ScriptElementConstructorWithSupports = typeof HTMLScriptElement & {
  supports?: (type: string) => boolean
}

/**
 * Chromium 121+ only (Chrome/Edge). Safari and Firefox lack
 * `HTMLScriptElement.supports` or return false for 'speculationrules':
 * either way we bail out BEFORE touching the DOM — no script element is
 * ever created on unsupported browsers.
 */
const supportsSpeculationRules = (): boolean => {
  try {
    if (typeof HTMLScriptElement === 'undefined') return false
    const scriptElement = HTMLScriptElement as ScriptElementConstructorWithSupports
    return scriptElement.supports?.('speculationrules') === true
  } catch (err) {
    return false
  }
}

/**
 * Injects the speculation rules script once. Returns true when the rules are
 * active (freshly injected or already present), false when unsupported.
 * Never throws: any DOM failure degrades to plain (non-speculative) navigation.
 */
export const installArcSpeculationRules = (): boolean => {
  try {
    if (!supportsSpeculationRules()) return false
    if (document.getElementById(SCRIPT_ID)) return true

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.type = 'speculationrules'
    script.textContent = JSON.stringify(buildSpeculationRules())
    document.head.appendChild(script)
    return true
  } catch (err) {
    return false
  }
}

/** Removes the speculation rules script (no-op when absent). Never throws. */
export const removeArcSpeculationRules = (): void => {
  try {
    document.getElementById(SCRIPT_ID)?.remove()
  } catch (err) {
    // Ignore: worst case the inert rules script stays in <head>
  }
}
